import type { TFunction } from 'i18next';
import type {
  AssessedArea,
  HmsRiskLevel,
  InsufficientAssessment,
  InsufficientAssessmentError,
  InsufficientAssessor,
  Placement,
  ReviewParticipation,
  RiskAssessment,
  RiskAssessmentError,
  RiskAssessmentRequest,
  SafetyRepMissing,
} from '../../api/workEnvironmentTypes';

import { sameDay } from './riskView';
import { dayAndMonth } from './surveyProcessView';

export type FieldError = { fieldId: string; message: string };

/*
 * The rules of the assessments live in the API. The page shows the errors the API returns,
 * next to the fields and in the error summary.
 */

/** The errors the API returned for a form, or null when the request failed for another reason */
export const validationErrors = <T>(error: unknown): T[] | null => {
  const response = (error as { response?: { status?: number; data?: { errors?: unknown } } } | null)?.response;
  return response?.status === 400 && Array.isArray(response.data?.errors) ? (response.data.errors as T[]) : null;
};

export type PlacementDraft = Placement & { reason: string };

export type AssessmentForm = {
  participation: ReviewParticipation | null;
  representativeRole: string;
  absenceReason: string;
  shortLongTerm: string;
  combined: string;
  /** Only the areas the user has moved. The others stay in the calculated cell. */
  placements: Record<string, PlacementDraft>;
};

export const assessmentForm = (saved: RiskAssessment | null): AssessmentForm => ({
  participation: saved?.participation ?? null,
  representativeRole: saved?.representativeRole ?? '',
  absenceReason: saved?.absenceReason ?? '',
  shortLongTerm: saved?.shortLongTerm ?? '',
  combined: saved?.combined ?? '',
  placements: Object.fromEntries(
    (saved?.adjustments ?? []).map(({ factorKey, ...draft }) => [factorKey, draft]),
  ),
});

const samePlacement = (a: Placement, b: Placement) =>
  a.consequence === b.consequence && a.likelihood === b.likelihood;

/** The cell the form shows for an area: the moved one, or the calculated one */
export const draftPlacement = (area: AssessedArea, form: AssessmentForm): PlacementDraft =>
  form.placements[area.key] ?? { consequence: area.consequence, likelihood: area.likelihood, reason: '' };

export const isMoved = (area: AssessedArea, form: AssessmentForm) => !samePlacement(area, draftPlacement(area, form));

export const movedCount = (areas: AssessedArea[], form: AssessmentForm) =>
  areas.filter((area) => isMoved(area, form)).length;

/** Null moves the area back to the calculated cell */
export const withPlacement = (
  form: AssessmentForm,
  factorKey: string,
  draft: PlacementDraft | null,
): AssessmentForm => ({
  ...form,
  placements: Object.fromEntries([
    ...Object.entries(form.placements).filter(([key]) => key !== factorKey),
    ...(draft ? [[factorKey, draft] as const] : []),
  ]),
});

export const assessmentRequest = (form: AssessmentForm, areas: AssessedArea[]): RiskAssessmentRequest => ({
  participation: form.participation,
  representativeRole: form.participation === 'EMPLOYEE_REP' ? form.representativeRole : null,
  absenceReason: form.participation === 'NONE' ? form.absenceReason : null,
  shortLongTerm: form.shortLongTerm,
  combined: form.combined,
  adjustments: areas
    .filter((area) => isMoved(area, form))
    .map((area) => ({ factorKey: area.key, ...draftPlacement(area, form) })),
});

/** The team is represented by a safety representative, its own or a main one */
export const hasSafetyRep = (assessment: { safetyReps: string[] } | null | undefined) =>
  (assessment?.safetyReps.length ?? 0) > 0;

/** A team leader who is the only one representing the team cannot review their own work as safety representative */
export const reviewerMissing = (assessment: { safetyRepMissing: SafetyRepMissing | null }) =>
  assessment.safetyRepMissing === 'leaderIsSafetyRep' ? 'leaderIsRep' : 'noRep';

/** A team with a safety representative goes through the risk with them, others with someone who represents the employees */
export const participationOptions = (hasSafetyRep: boolean): ReviewParticipation[] =>
  hasSafetyRep ? ['SAFETY_REP', 'NONE'] : ['EMPLOYEE_REP', 'NONE'];

/**
 * The texts about going on alone differ with and without a safety representative. Where the
 * organisation does not use them, the team leader assesses alone.
 */
export const reviewTexts = (hasSafetyRep: boolean, usesSafetyReps = true) => {
  if (!usesSafetyReps) return 'teamLeader';
  return hasSafetyRep ? 'withSafetyRep' : 'withoutSafetyRep';
};

export const MATRIX_SCALE = [1, 2, 3, 4, 5];

export const ASSESSMENT_FIELDS = {
  participation: 'assessment-participation',
  absenceReason: 'assessment-absence-reason',
  shortLongTerm: 'assessment-short-long-term',
  combined: 'assessment-combined',
  adjustment: (factorKey: string) => `assessment-consequence-${factorKey}`,
  adjustmentReason: (factorKey: string) => `assessment-adjustment-reason-${factorKey}`,
};

export const assessmentFieldErrors = (
  errors: RiskAssessmentError[],
  areas: AssessedArea[],
  t: TFunction,
): FieldError[] =>
  errors.map((error) => {
    if (!('factorKey' in error))
      return {
        fieldId: ASSESSMENT_FIELDS[error.type],
        message: t(`workEnvironmentRisk.assessment.errors.${error.type}`),
      };
    const name = areas.find((area) => area.key === error.factorKey)?.name ?? error.factorKey;
    return {
      fieldId: ASSESSMENT_FIELDS[error.type](error.factorKey),
      message: t(`workEnvironmentRisk.assessment.errors.${error.type}`, { name }),
    };
  });

const participationSummary = (assessment: RiskAssessment, hasSafetyRep: boolean, t: TFunction) => {
  if (assessment.participation === 'SAFETY_REP') return t('workEnvironmentRisk.assessment.summary.safetyRep');
  if (assessment.participation === 'TEAM_LEADER') return t('workEnvironmentRisk.assessment.summary.teamLeader');
  if (assessment.participation === 'NONE')
    return t(`workEnvironmentRisk.assessment.summary.alone.${reviewTexts(hasSafetyRep)}`);
  return assessment.representativeRole
    ? t('workEnvironmentRisk.assessment.summary.employeeRepWithRole', { role: assessment.representativeRole })
    : t('workEnvironmentRisk.assessment.summary.employeeRep');
};

/** «Verneombudet deltok. 2 områder er justert.» */
export const assessmentSummary = (assessment: RiskAssessment, hasSafetyRep: boolean, t: TFunction) =>
  [
    participationSummary(assessment, hasSafetyRep, t),
    assessment.adjustments.length > 0 &&
      t('workEnvironmentRisk.assessment.summary.adjusted', { count: assessment.adjustments.length }),
  ]
    .filter(Boolean)
    .join(' ');

/** «Konsekvens 5, sannsynlighet 5 · Høy risiko» */
export const placementText = (placement: Placement & { level: HmsRiskLevel }, t: TFunction) =>
  t('workEnvironmentRisk.assessment.placement', {
    consequence: placement.consequence,
    likelihood: placement.likelihood,
    level: t(`workEnvironmentRisk.matrix.levels.${placement.level}`),
  });

/** «Varslet 30. september», or when the safety representatives were last reminded */
export const reminderText = (
  remindedAt: string | null,
  notifiedAt: string,
  t: TFunction,
  language: string,
  now = new Date(),
) => {
  if (!remindedAt) return t('workEnvironmentRisk.assessment.rep.notified', { date: dayAndMonth(notifiedAt, language) });
  return sameDay(new Date(remindedAt), now)
    ? t('workEnvironmentRisk.assessment.rep.remindedToday')
    : t('workEnvironmentRisk.assessment.rep.reminded', { date: dayAndMonth(remindedAt, language) });
};

export const mailtoLink = (emails: string[]) => `mailto:${emails.join(',')}`;

export type InsufficientForm = {
  assessedBy: InsufficientAssessor | null;
  interpretation: string;
  alternative: string;
};

/** The team leader decides alone where the organisation does not use safety representatives */
export const decidesAlone = (assessors: InsufficientAssessor[]) =>
  assessors.length === 1 && assessors[0] === 'TEAM_LEADER';

/**
 * «…NoRep» for the texts of a team without a safety representative, «…LeaderIsRep» where the team leader is the only
 * one, so no one else can assess it, and «…NotUsed» where there are none
 */
export const insufficientTextSuffix = (assessors: InsufficientAssessor[], missing?: SafetyRepMissing | null) => {
  if (decidesAlone(assessors)) return 'NotUsed';
  if (assessors.includes('SAFETY_REP')) return '';
  return missing === 'leaderIsSafetyRep' ? 'LeaderIsRep' : 'NoRep';
};

export const insufficientForm = (saved: InsufficientAssessment | null): InsufficientForm => ({
  assessedBy: saved?.assessedBy ?? null,
  interpretation: saved?.interpretation ?? '',
  alternative: saved?.alternative ?? '',
});

export const INSUFFICIENT_FIELDS: Record<InsufficientAssessmentError, string> = {
  assessedBy: 'insufficient-assessed-by-SAFETY_REP',
  interpretation: 'insufficient-interpretation',
  alternative: 'insufficient-alternative',
};

export const insufficientFieldErrors = (errors: InsufficientAssessmentError[], t: TFunction): FieldError[] =>
  errors.map((error) => ({
    fieldId: INSUFFICIENT_FIELDS[error],
    message: t(`workEnvironmentRisk.insufficient.errors.${error}`),
  }));

const DAY_MS = 24 * 60 * 60 * 1000;

const isoDay = (date: Date) =>
  [date.getFullYear(), date.getMonth() + 1, date.getDate()].map((part) => String(part).padStart(2, '0')).join('-');

/** From tomorrow until the longest extension allowed, counting the whole last day */
export const deadlineBounds = (now: Date, maxDays: number) => ({
  min: isoDay(new Date(now.getTime() + DAY_MS)),
  max: isoDay(new Date(now.getTime() + (maxDays - 1) * DAY_MS)),
});

/** The team can answer through the whole chosen day */
export const endOfDay = (day: string) => new Date(`${day}T23:59:00`).toISOString();
